/**
 * 1.81.0 · Servidor local: el servidor atiende la base de la sucursal para los puestos.
 *  · q / x: una consulta o una escritura suelta.
 *  · tx.begin / tx.q / tx.x / tx.end: una transacción de escritura del puesto (una venta con sus ítems,
 *    pagos, caja y stock va entera o no va). Mientras dura, las demás escrituras esperan; si el puesto
 *    no manda el paso siguiente en 5 segundos, no la termina en 30 o se desconecta, se deshace (1.99.0: antes
 *    20 segundos entre pasos y sin tope: una venta trabada frenaba a todos los puestos).
 *  · tx.status (1.86.0): si el puesto mandó «confirmar» y se cortó antes de la respuesta, pregunta con la clave
 *    de la transacción si quedó guardada (así no se carga dos veces la misma venta).
 *  · Cuando cambia una tabla (por un puesto, por el servidor o por lo que baja de la nube) se avisa a
 *    todos los puestos ("ch") para que vuelvan a leer lo que muestran.
 */

export interface HostTx {
  getAll<T = unknown>(sql: string, params?: unknown[]): Promise<T[]>;
  execute(sql: string, params?: unknown[]): Promise<{ rowsAffected?: number; insertId?: number }>;
}
export interface HostDb extends HostTx {
  writeTransaction<T>(cb: (tx: HostTx) => Promise<T>): Promise<T>;
}

const TX_IDLE_MS = 5_000;
/** 1.99.0: pasado este tiempo, una transacción de un puesto se deshace si alguien más espera la base. */
const TX_MAX_MS = 30_000;
/** Y nunca dura más que esto (una importación grande se hace por partes). */
const TX_HARD_MS = 10 * 60_000;
/** Cuánto se recuerda si una transacción quedó guardada (para el puesto que se cortó al confirmar). */
const OUTCOME_MS = 15 * 60_000;

interface OpenTx {
  owner: string;
  cap?: ReturnType<typeof setTimeout>;
  /** Próximo paso que pide el puesto (lo atiende la transacción abierta). */
  next: (step: Step) => void;
  timer: ReturnType<typeof setTimeout>;
}
type Step =
  | { k: "q"; sql: string; params: unknown[]; ok: (v: unknown) => void; ko: (e: unknown) => void }
  | { k: "x"; sql: string; params: unknown[]; ok: (v: unknown) => void; ko: (e: unknown) => void }
  | { k: "end"; commit: boolean; ok: (v: unknown) => void; ko: (e: unknown) => void }
  | { k: "abort" };

class Rollback extends Error { constructor() { super("Transacción deshecha"); } }

const asParams = (p: unknown): unknown[] => (Array.isArray(p) ? p : []);
const asSql = (s: unknown): string => {
  if (typeof s !== "string" || !s.trim()) throw new Error("Consulta vacía");
  return s;
};
/**
 * 1.81.22 · Lo que un puesto puede escribir en la base del servidor: altas, cambios y bajas de datos.
 * No: cambiar la estructura (CREATE/DROP/ALTER…), adjuntar otra base (ATTACH), PRAGMA, varias sentencias
 * juntas, ni tocar las tablas internas de la sincronización (ps_*) o de SQLite (sqlite_*), que harían
 * perder lo que todavía no se subió a la nube.
 */
/**
 * 1.99.15 · Funciones de la sincronización (powersync_clear borra la cola de lo que falta subir) o para cargar
 * extensiones: no las puede llamar un puesto.
 */
const internalFn = (body: string) => /\b(powersync_\w*|load_extension|ps_\w*|sqlite_\w*)\s*\(/i.test(body.replace(/\/\*[\s\S]*?\*\//g, " "));

export function asWriteSql(s: unknown): string {
  const sql = asSql(s);
  const body = sql.replace(/'(?:[^']|'')*'/g, "''").replace(/--[^\n]*/g, " ").trim();
  if (!/^(insert|update|delete|replace|with|select)\b/i.test(body)) throw new Error("Escritura no permitida desde un puesto");
  if (/;\s*\S/.test(body)) throw new Error("Una sola sentencia por pedido");
  if (/\b(attach|detach|pragma|vacuum|reindex)\b/i.test(body) || /\b(ps_[a-z0-9_]+|sqlite_[a-z0-9_]+)\b/i.test(body) || internalFn(body)) {
    throw new Error("Escritura no permitida desde un puesto");
  }
  return sql;
}
/** 1.81.22 · Lecturas de un puesto: una consulta, sin escribir ni tocar la estructura. */
export function asReadSql(s: unknown): string {
  const sql = asSql(s);
  const body = sql.replace(/'(?:[^']|'')*'/g, "''").replace(/--[^\n]*/g, " ").trim();
  if (!/^(select|with|explain)\b/i.test(body)) throw new Error("Consulta no permitida desde un puesto");
  if (/;\s*\S/.test(body)) throw new Error("Una sola sentencia por pedido");
  // 1.99.15: una escritura en cualquier parte de la consulta (antes pasaban «WITH … INSERT OR REPLACE», «REPLACE INTO»,
  // «UPDATE OR IGNORE»), y las funciones internas de la sincronización.
  if (/\b(attach|detach|pragma|vacuum|reindex)\b/i.test(body) || /\b(insert|delete)\b|\breplace\s+into\b|\bupdate\s+(or\s+\w+\s+)?["`\[]?[\w.]+["`\]]?\s+set\b/i.test(body) || internalFn(body)) {
    throw new Error("Consulta no permitida desde un puesto");
  }
  return sql;
}
const execResult = (r: { rowsAffected?: number; insertId?: number }) => ({ rowsAffected: r.rowsAffected ?? 0, insertId: r.insertId ?? null });

export class DbHost {
  private txs = new Map<string, OpenTx>();
  private seq = 0;
  /** Clave que manda el puesto → ¿quedó guardada? (se resuelve al terminar). */
  private outcomes = new Map<string, { at: number; done: Promise<boolean> }>();
  constructor(private db: HostDb, private opts: { idleMs?: number; maxMs?: number } = {}) {}

  /** 1.99.0: cuánto esperaron las transacciones de los puestos para tomar la base (para la pantalla del servidor). */
  private waits: { at: number; ms: number }[] = [];
  private undone: number[] = [];
  /** Transacciones pedidas que todavía no tomaron la base (alguien espera). */
  private waiting = 0;
  stats(): { maxWaitMs: number; slowWaits: number; undone: number } {
    const since = Date.now() - 3600_000;
    this.waits = this.waits.filter((w) => w.at >= since);
    this.undone = this.undone.filter((t) => t >= since);
    return { maxWaitMs: this.waits.reduce((m, w) => Math.max(m, w.ms), 0), slowWaits: this.waits.filter((w) => w.ms >= 2000).length, undone: this.undone.length };
  }

  /** Pedido de un puesto (`owner` = su conexión). */
  async handle(owner: string, m: string, a: unknown): Promise<unknown> {
    const x = (a ?? {}) as { sql?: unknown; params?: unknown; tx?: unknown; commit?: unknown };
    switch (m) {
      case "q": return this.db.getAll(asReadSql(x.sql), asParams(x.params));
      case "x": return execResult(await this.db.execute(asWriteSql(x.sql), asParams(x.params)));
      case "tx.begin": return this.begin(owner, typeof (a as { key?: unknown } | null)?.key === "string" ? String((a as { key: string }).key).slice(0, 64) : null);
      case "tx.status": {
        const key = typeof (a as { key?: unknown } | null)?.key === "string" ? String((a as { key: string }).key).slice(0, 64) : "";
        const o = this.outcomes.get(key);
        if (o) return o.done;
        // 1.99.19: después de reiniciarse, el servidor no la tiene en memoria: se mira la base (la clave se guarda en la
        // misma transacción). Antes contestaba «no» y el puesto cobraba de nuevo una venta que sí había quedado.
        if (!key) return false;
        const row = await this.db.getAll<{ id: string }>("SELECT id FROM local_tx_keys WHERE id = ?", [key]).catch(() => []);
        return row.length > 0;
      }
      case "tx.q":
      case "tx.x": {
        const t = this.own(owner, x.tx);
        return new Promise((ok, ko) => t.next({ k: m === "tx.q" ? "q" : "x", sql: m === "tx.q" ? asReadSql(x.sql) : asWriteSql(x.sql), params: asParams(x.params), ok, ko }));
      }
      case "tx.end": {
        const t = this.own(owner, x.tx);
        return new Promise((ok, ko) => t.next({ k: "end", commit: x.commit === true, ok, ko }));
      }
      default: throw new Error(`Pedido desconocido: ${m}`);
    }
  }

  /** El puesto se desconectó: lo que tenía abierto se deshace. */
  dropOwner(owner: string) {
    // 1.94.0: también la que todavía esperaba su turno (se deshace apenas la base la tome).
    this.gone.add(owner);
    for (const t of [...this.txs.values()]) if (t.owner === owner) t.next({ k: "abort" });
  }
  /** Conexiones cerradas (sus transacciones en espera no se abren). Cada conexión tiene un id nuevo. */
  private gone = new Set<string>();

  openTransactions() { return this.txs.size; }

  /** 1.99.0: algo del servidor que necesita la base (números de venta, contadores): cuenta como «esperando». */
  async needsDb<T>(fn: () => Promise<T>): Promise<T> {
    this.waiting++;
    try { return await fn(); } finally { this.waiting--; }
  }

  private own(owner: string, id: unknown): OpenTx {
    const t = typeof id === "string" ? this.txs.get(id) : undefined;
    if (!t || t.owner !== owner) throw new Error("La transacción ya no está abierta (se deshizo por demora o por un corte)");
    return t;
  }

  /** Abre la transacción y responde con su id recién cuando la base la tomó (puede esperar a otra). */
  private begin(owner: string, key: string | null): Promise<string> {
    const id = `t${++this.seq}`;
    const idle = this.opts.idleMs ?? TX_IDLE_MS;
    const now = Date.now();
    for (const [k, o] of this.outcomes) if (now - o.at > OUTCOME_MS) this.outcomes.delete(k);
    let settle: (v: boolean) => void = () => undefined;
    if (key && !this.outcomes.has(key)) this.outcomes.set(key, { at: now, done: new Promise<boolean>((r) => { settle = r; }) });
    this.waiting++;
    return new Promise<string>((started, failed) => {
      const inbox: Step[] = [];
      let wake: (() => void) | null = null;
      const t: OpenTx = {
        owner,
        next: (s) => {
          // El reloj entre pasos corre desde que se contestó el paso anterior (una consulta larga no lo gasta).
          clearTimeout(t.timer);
          inbox.push(s);
          wake?.();
        },
        timer: setTimeout(() => undefined, 0),
      };
      const take = async (): Promise<Step> => {
        while (!inbox.length) await new Promise<void>((r) => { wake = r; });
        wake = null;
        return inbox.shift()!;
      };
      let counted = true;
      const unwait = () => { if (counted) { counted = false; this.waiting--; } };
      void this.db.writeTransaction(async (tx) => {
        unwait();
        if (this.gone.has(owner)) throw new Rollback();
        this.waits.push({ at: Date.now(), ms: Date.now() - now });
        if (this.waits.length > 500) this.waits.splice(0, this.waits.length - 500);
        this.txs.set(id, t);
        let closed = false, stepped = false;
        const close = () => { closed = true; clearTimeout(t.timer); if (t.cap) clearTimeout(t.cap); };
        const giveUp = () => { if (closed) return; if (stepped) this.undone.push(Date.now()); t.next({ k: "abort" }); };
        t.timer = setTimeout(giveUp, idle);
        // Tope: pasados 30 s se deshace solo si otro puesto (o el servidor) está esperando la base. Sin nadie
        // esperando sigue (un inventario o una importación grande desde un puesto lleva más), hasta 10 minutos.
        const started0 = Date.now();
        const checkCap = () => {
          if (closed) return;
          if (this.waiting > 0 || Date.now() - started0 >= TX_HARD_MS) giveUp();
          else t.cap = setTimeout(checkCap, 2_000);
        };
        t.cap = setTimeout(checkCap, this.opts.maxMs ?? TX_MAX_MS);
        started(id);
        for (;;) {
          const s = await take();
          if (s.k === "abort") { close(); throw new Rollback(); }
          if (s.k === "end") {
            close();
            if (!s.commit) { s.ok(null); throw new Rollback(); }
            // 1.99.19: la clave del puesto queda en la base con lo que guardó (si el servidor se reinicia antes de
            // contestar, `tx.status` la encuentra). Se olvida al día.
            if (key) {
              try {
                await tx.execute("INSERT OR REPLACE INTO local_tx_keys (id, at) VALUES (?, ?)", [key, new Date().toISOString()]);
                if (Math.random() < 0.02) await tx.execute("DELETE FROM local_tx_keys WHERE at < ?", [new Date(Date.now() - 86_400_000).toISOString()]);
              } catch { /* sin la tabla (no debería pasar): queda solo en memoria, como antes */ }
            }
            // El ok va después de que la base confirme (ver abajo).
            (t as OpenTx & { committed?: Step }).committed = s;
            return;
          }
          stepped = true;
          try {
            if (s.k === "q") s.ok(await tx.getAll(s.sql, s.params));
            else s.ok(execResult(await tx.execute(s.sql, s.params)));
          } catch (e) {
            // Un error adentro deja la transacción como estaba: el puesto decide (normalmente la deshace).
            s.ko(e);
          }
          if (!inbox.length && !closed) t.timer = setTimeout(giveUp, idle);
        }
      }).then(() => {
        const c = (t as OpenTx & { committed?: Step }).committed;
        settle(!!c);
        if (c && c.k === "end") c.ok(null);
      }, (e) => {
        unwait();
        settle(false);
        if (!this.txs.has(id)) failed(e);
        const c = (t as OpenTx & { committed?: Step }).committed;
        if (c && c.k === "end") c.ko(e);
        // Lo que quedó pendiente en la cola falla.
        for (const s of inbox.splice(0)) if (s.k !== "abort") s.ko(new Error("La transacción se deshizo"));
      }).finally(() => {
        clearTimeout(t.timer);
        if (t.cap) clearTimeout(t.cap);
        this.txs.delete(id);
      });
    });
  }
}

/** Tablas que nombra una consulta (para saber cuándo volver a leerla). */
export function tablesIn(sql: string, known: ReadonlySet<string>): string[] {
  const words = new Set(sql.toLowerCase().match(/[a-z_][a-z0-9_]*/g) ?? []);
  return [...known].filter((t) => words.has(t));
}
